import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Crown, Check, Loader2, Zap, BarChart3, BellRing, MapPin, Sparkles, TrendingUp, ArrowRight, RotateCcw, Building2, Rocket, Link as LinkIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { useToast } from "../hooks/use-toast";
import { startCheckout, isProActive, planTier } from "../utils/checkout";
import { PromoCodeField, promoPrice } from "../components/PromoCodeField";
import { AddonShelf } from "../components/pricing/AddonShelf";
import { TopUpShelf } from "../components/pricing/TopUpShelf";
import { LoyaltyOffer } from "../components/dashboard/LoyaltyOffer";
import api from "../api";

const PRICES = { pro: "$19", proYear: "$190", proYearMo: "~$15.83", plus: "$39", plusYear: "$390", plusYearMo: "$32.50", boost: "$10" };
// A code discounts the term it's used on; nothing auto-charges later, so the next term is list price.
const LIST = { pro_30d: 19, pro_annual: 190, business_plus_30d: 39, business_plus_annual: 390 };

const Premium = () => {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState("");
  const [promo, setPromo] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [credits, setCredits] = useState(null);
  const [cfg, setCfg] = useState(null);
  const tier = planTier(user);
  const paid = isProActive(user);

  const proFeatures = [{ icon: Zap, k: "prc.p1" }, { icon: BarChart3, k: "prc.p2" }, { icon: MapPin, k: "prc.p3" },
    { icon: BellRing, k: "prc.p4" }, { icon: Rocket, k: "prc.p5" }];
  const plusFeatures = [{ icon: Zap, k: "prc.b1" }, { icon: Building2, k: "prc.b2" }, { icon: TrendingUp, k: "prc.b3" },
    { icon: LinkIcon, k: "prc.b5" }, { icon: Sparkles, k: "prc.b4" }];

  useEffect(() => {
    if (user?.role === "business") api.get("/my/credits").then((r) => setCredits(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)).catch(() => setCfg({})); }, []);

  const planLabelOf = (p) => (p === "business_plus" ? t("prc.plus") : p === "pro" ? t("prc.pro") : t("prc.free"));

  // No proration anywhere on LytePress, so a plan change waits for the term the shop already paid for.
  const schedulePlanChange = async (plan) => {
    setLoading(`sw-${plan}`);
    try {
      const { data } = await api.post("/my/subscription/plan-change", { plan });
      await refresh();
      toast({ title: t("prc.scheduled", { plan: planLabelOf(plan) }),
        description: t("prc.scheduledOn", { date: new Date(data.pendingPlanAt).toLocaleDateString() }) });
    } catch (e) { toast({ title: e.response?.data?.detail || t("prc.tryAgain") }); }
    finally { setLoading(""); }
  };

  const cancelPlanChange = async () => {
    setLoading("sw-cancel");
    try {
      await api.post("/my/subscription/plan-change/cancel");
      await refresh();
      toast({ title: t("prc.scheduleCancelled") });
    } catch (e) { toast({ title: e.response?.data?.detail || t("prc.tryAgain") }); }
    finally { setLoading(""); }
  };

  const SwitchAtEnd = ({ plan, dark = false }) => {
    if (user?.role !== "business" || !paid || tier === plan || user?.planLifetime) return null;
    const pending = user?.pendingPlan === plan;
    return (
      <div className="mt-3 text-center">
        <button onClick={() => (pending ? cancelPlanChange() : schedulePlanChange(plan))}
          disabled={loading === `sw-${plan}` || loading === "sw-cancel"} data-testid={`switch-plan-${plan}-btn`}
          className={`w-full text-xs font-bold py-2.5 rounded-xl border transition-colors disabled:opacity-60 ${dark
            ? "border-white/30 text-white hover:bg-white/10"
            : "border-border text-foreground hover:border-[#16a34a] hover:text-[#16a34a]"}`}>
          {pending ? t("prc.cancelScheduled") : t("prc.switchAtTermEnd", { plan: planLabelOf(plan) })}
        </button>
        <p className={`mt-1.5 text-[11px] ${dark ? "text-white/70" : "text-muted-foreground"}`}>{t("prc.noProration")}</p>
      </div>
    );
  };

  const toggleRenew = async () => {
    try {
      await api.post(user?.autoRenew ? "/my/subscription/cancel" : "/my/subscription/resume");
      await refresh();
      toast({ title: user?.autoRenew ? t("prc.renewOff") : t("prc.renewOn") });
    } catch { toast({ title: t("prc.subFail") }); }
  };

  const upgrade = async (pkg) => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "business") { toast({ title: t("prc.bizOnly"), description: t("prc.bizOnlyMsg") }); return; }
    setLoading(pkg);
    try { await startCheckout(pkg, { code: promo?.code }); }
    catch { toast({ title: t("prc.checkoutFail"), description: t("prc.tryAgain") }); setLoading(""); }
  };

  const planName = tier === "business_plus" ? t("prc.plus") : tier === "pro" ? t("prc.pro") : t("prc.free");

  // What they pay now with a code, and what the term after costs — said before they buy.
  const firstTermNote = (pkg) => {
    const full = LIST[pkg];
    const now = promoPrice(full, promo, pkg);
    if (now == null || now >= full) return null;
    return t("prc.firstTermOnly", { price: `$${now}`, full: `$${full}` });
  };

  const renewLine = user?.planLifetime
    ? t("prc.activeLife")
    : (user?.autoRenew ? t("prc.renewsOn", { date: new Date(user?.planExpiresAt).toLocaleDateString() })
      : t("prc.endsOn", { date: new Date(user?.planExpiresAt).toLocaleDateString() }));

  return (
    <div className="space-y-8 lp-fade-up max-w-6xl" data-testid="premium-page">
      <div className="relative overflow-hidden rounded-3xl bg-[#0f2a1a] text-white p-7 sm:p-10" data-testid="premium-hero">
        <div className="absolute -right-16 -top-20 h-72 w-72 rounded-full bg-[#16a34a]/40 blur-3xl" />
        <div className="absolute left-1/4 -bottom-16 h-52 w-52 rounded-full bg-[#f5b942]/20 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-emerald-200 backdrop-blur"><Crown size={13} className="text-[#f5b942]" /> {t("prc.badge")}</span>
          <h1 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05]">{t("prc.h1")}</h1>
          <p className="mt-3 max-w-lg text-sm sm:text-base text-emerald-100/80">{t("prc.sub")}</p>
          <div className="mt-5 flex flex-wrap gap-2 text-sm">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/95 px-4 py-1.5 font-bold text-[#0f2a1a]"><TrendingUp size={14} /> {t("prc.chipFeatured")}</span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/25 px-4 py-1.5 font-semibold text-white/90"><BarChart3 size={14} /> {t("prc.chipAnalytics")}</span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/25 px-4 py-1.5 font-semibold text-white/90"><Rocket size={14} /> {t("prc.chipCredits")}</span>
          </div>
        </div>
      </div>

      <LoyaltyOffer />

      {credits && (
        <div className="bg-card border border-border rounded-2xl p-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-3 gap-y-1 text-sm" data-testid="plan-usage">
          <span className="font-bold text-foreground">{t("prc.yourPlan", { plan: planName })}</span>
          <span className="text-muted-foreground">
            {t("prc.dealsUsed", { used: credits.dealsThisMonth, limit: credits.monthlyAllowance == null ? t("prc.unlimited") : (credits.allowanceTotal != null ? credits.allowanceTotal : credits.monthlyAllowance) })}
          </span>
          {credits.rolloverCredits > 0 && <span className="text-[#16a34a] font-semibold" data-testid="plan-rolled-in"><span className="hidden sm:inline">· </span>{t("prc.rolledIn", { n: credits.rolloverCredits })}</span>}
          {credits.dealCredits > 0 && <span className="text-[#16a34a] font-semibold" data-testid="plan-extra-credits"><span className="hidden sm:inline">· </span>{t("pc.nCreditsHeld", { n: credits.dealCredits })}</span>}
          {credits.boostPasses > 0 && <span className="text-[#16a34a] font-semibold" data-testid="plan-boost-passes"><span className="hidden sm:inline">· </span>{t("sr.passesHeld", { n: credits.boostPasses })}</span>}
        </div>
      )}

      {user?.pendingPlan && (
        <div className="rounded-2xl border border-[#f5b942] bg-[#fdf6e3] dark:bg-[#3a2f10] p-4 flex flex-wrap items-center gap-3" data-testid="pending-plan-banner">
          <p className="text-sm font-semibold text-foreground flex-1 min-w-[220px]">
            {t("prc.scheduledBanner", { plan: planLabelOf(user.pendingPlan),
              date: user.pendingPlanAt ? new Date(user.pendingPlanAt).toLocaleDateString() : "" })}
          </p>
          <button onClick={cancelPlanChange} disabled={loading === "sw-cancel"} data-testid="cancel-scheduled-plan-btn"
            className="text-xs font-bold border border-border bg-card hover:border-[#16a34a] px-3 py-2 rounded-xl transition-colors disabled:opacity-60">
            {t("prc.cancelScheduled")}
          </button>
        </div>
      )}

      <div className="flex justify-center">
        <div className="inline-flex max-w-full items-center gap-1 bg-secondary rounded-full p-1" data-testid="billing-toggle">
          <button onClick={() => setAnnual(false)} data-testid="billing-monthly" className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap transition-all ${!annual ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>{t("prc.monthly")}</button>
          <button onClick={() => setAnnual(true)} data-testid="billing-yearly" className={`px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${annual ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>{t("prc.yearly")} <span className="text-xs font-extrabold text-[#16a34a]">{t("prc.save17")}</span></button>
        </div>
      </div>

      <p className="-mt-4 text-center text-xs text-muted-foreground" data-testid="usd-billing-note">{t("prc.usdNote")}</p>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5 items-stretch">
        {/* Free */}
        <div className="bg-card border border-border rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid="plan-free">
          <h3 className="font-bold text-foreground text-lg">{t("prc.free")}</h3>
          <p className="text-4xl font-extrabold text-foreground mt-2">$0</p>
          <p className="text-sm text-muted-foreground mb-5">{t("prc.freeSub")}</p>
          <ul className="space-y-3 text-sm">
            {["prc.f1", "prc.f2", "prc.f3"].map((k) => (
              <li key={k} className="flex items-center gap-2.5 text-muted-foreground"><span className="h-5 w-5 rounded-full bg-secondary flex items-center justify-center shrink-0"><Check size={13} className="text-[#16a34a]" /></span> {t(k)}</li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-muted-foreground flex items-start gap-1.5" data-testid="roll-note-free">
            <RotateCcw size={12} className="text-muted-foreground mt-0.5 shrink-0" /> {t("prc.rollFree")}
          </p>
          {user?.role === "business" && tier === "free" && <p className="mt-6 text-xs font-bold text-muted-foreground">{t("prc.currentPlan")}</p>}
          {!user && (
            <Link to="/register" data-testid="free-start-btn"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-[#0f2a1a] hover:bg-[#16341f] text-white font-bold py-3 rounded-xl transition-colors">
              {t("prc.startFree")}
            </Link>
          )}
          <SwitchAtEnd plan="free" />
        </div>

        {/* Pro */}
        <div className="group relative overflow-hidden rounded-3xl p-7 bg-card border-2 border-[#f5b942] transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl" data-testid="plan-pro">
          <h3 className="font-bold text-foreground text-lg flex items-center gap-2"><Crown size={18} className="text-[#f5b942]" /> {t("prc.pro")}</h3>
          <div className="mt-2 min-h-[52px]">
            <p key={annual ? "y" : "m"} className="lp-fade text-4xl font-extrabold text-foreground">
              {annual ? PRICES.proYear : PRICES.pro}<span className="text-base font-semibold text-muted-foreground"> {annual ? t("prc.perYear") : t("prc.per30")}</span>
            </p>
            <p className="text-sm text-muted-foreground mt-0.5">{annual ? t("prc.proYearNote", { monthly: PRICES.proYearMo }) : t("prc.proMonthNote", { yearly: PRICES.proYear })}</p>
          </div>
          <ul className="space-y-3 text-sm mt-5 mb-6">
            {proFeatures.map((f) => (
              <li key={f.k} className="flex items-center gap-2.5 text-foreground"><span className="h-5 w-5 rounded-full bg-[#f5b942]/15 flex items-center justify-center shrink-0"><f.icon size={12} className="text-[#f5b942]" /></span> {t(f.k)}</li>
            ))}
          </ul>
          <div className="-mt-3 mb-6 rounded-xl bg-[#f5b942]/10 px-3 py-2.5 text-xs text-muted-foreground" data-testid="roll-note-pro">
            <p className="flex items-start gap-1.5 font-semibold text-foreground"><RotateCcw size={12} className="text-[#f5b942] mt-0.5 shrink-0" /> {t("prc.rollPro")}</p>
            <p className="mt-1 pl-[18px]">{t("prc.rollAddon")}</p>
          </div>
          {tier === "pro" ? (
            <div className="space-y-3" data-testid="pro-active-banner">
              <div className="w-full bg-[#f5b942]/15 text-[#b07d0a] dark:text-[#f5b942] font-semibold py-3 rounded-xl text-center flex items-center justify-center gap-2"><Check size={18} /> {renewLine}</div>
              {!user?.planLifetime && (
                <button onClick={toggleRenew} data-testid="toggle-autorenew-btn" className="w-full bg-secondary hover:bg-secondary/70 text-foreground text-sm font-semibold py-2.5 rounded-xl transition-colors">
                  {user?.autoRenew ? t("prc.cancelRenewPro") : t("prc.resumeRenew")}
                </button>
              )}
            </div>
          ) : (
            <button onClick={() => upgrade(annual ? "pro_annual" : "pro_30d")} disabled={!!loading}
              data-testid={annual ? "upgrade-pro-annual-btn" : "upgrade-pro-btn"}
              className="group/cta w-full bg-[#f5b942] hover:bg-[#e0a92e] disabled:opacity-60 text-[#0f2a1a] font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2">
              {loading.startsWith("pro") ? <Loader2 className="animate-spin" size={18} /> : <Crown size={18} />}
              {annual ? t("prc.goYearly", { price: PRICES.proYear }) : t("prc.goMonthly", { price: PRICES.pro })}
              <ArrowRight size={17} className="transition-transform duration-300 group-hover/cta:translate-x-1" />
            </button>
          )}
          {tier !== "pro" && firstTermNote(annual ? "pro_annual" : "pro_30d") && (
            <p className="mt-2 text-center text-xs font-semibold text-[#f5b942]" data-testid="pro-first-term-note">
              {firstTermNote(annual ? "pro_annual" : "pro_30d")}
            </p>
          )}
          {tier !== "pro" && (
            <p className="mt-2 text-center text-[11px] text-muted-foreground" data-testid="pro-fee-note">{t("prc.feeNote")}</p>
          )}
          <SwitchAtEnd plan="pro" />
          {!paid && (
            <p className="mt-3 text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5" data-testid="premium-guarantee">
              <RotateCcw size={12} className="text-[#f5b942]" /> {t("prc.guarantee")} <Link to="/terms" className="underline font-semibold hover:text-foreground">{t("prc.guaranteeLink")}</Link>
            </p>
          )}
        </div>

        {/* Elite */}
        <div className="group relative overflow-hidden rounded-3xl p-7 bg-[#0f2a1a] text-white shadow-lg shadow-emerald-900/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl" data-testid="plan-business-plus">
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#16a34a]/30 blur-2xl opacity-70 group-hover:opacity-100 transition-opacity duration-300" />
          <span className="absolute top-5 right-5 text-xs font-extrabold bg-[#f5b942] text-[#0f2a1a] px-2.5 py-1 rounded-full flex items-center gap-1" data-testid="plus-best-value"><Sparkles size={12} /> {t("prc.bestValue")}</span>
          <h3 className="font-bold text-lg flex items-center gap-2"><Building2 size={18} className="text-[#4ade80]" /> {t("prc.plus")}</h3>
          <div className="mt-2 min-h-[52px]">
            <p key={annual ? "yp" : "mp"} className="lp-fade text-4xl font-extrabold">
              {annual ? PRICES.plusYear : PRICES.plus}<span className="text-base font-semibold text-white/80"> {annual ? t("prc.perYear") : t("prc.per30")}</span>
            </p>
            <p className="text-sm text-white/80 mt-0.5">{annual ? t("prc.plusYearNote", { monthly: PRICES.plusYearMo }) : t("prc.plusMonthNote")}</p>
          </div>
          <ul className="space-y-3 text-sm mt-5 mb-6">
            <li className="flex items-center gap-2.5 font-bold" data-testid="plus-includes-pro">
              <span className="h-5 w-5 rounded-full bg-white/15 flex items-center justify-center shrink-0"><Check size={12} className="text-[#4ade80]" /></span>
              {t("prc.plusIncludesPro")}
            </li>
            {plusFeatures.map((f) => (
              <li key={f.k} className="flex items-center gap-2.5"><span className="h-5 w-5 rounded-full bg-white/15 flex items-center justify-center shrink-0"><f.icon size={12} className="text-[#4ade80]" /></span> {t(f.k)}</li>
            ))}
          </ul>
          <div className="-mt-3 mb-6 rounded-xl bg-white/10 px-3 py-2.5 text-xs text-white/80" data-testid="roll-note-plus">
            <p className="flex items-start gap-1.5 font-semibold text-white"><RotateCcw size={12} className="text-[#4ade80] mt-0.5 shrink-0" /> {t("prc.rollPlus")}</p>
            <p className="mt-1 pl-[18px]">{t("prc.rollAddon")}</p>
          </div>
          {tier === "business_plus" ? (
            <div className="space-y-3" data-testid="plus-active-banner">
              <div className="w-full bg-white/15 text-white font-semibold py-3 rounded-xl text-center flex items-center justify-center gap-2"><Check size={18} /> {renewLine}</div>
              {!user?.planLifetime && (
                <button onClick={toggleRenew} data-testid="toggle-autorenew-plus-btn" className="w-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors">
                  {user?.autoRenew ? t("prc.cancelRenew") : t("prc.resumeRenew")}
                </button>
              )}
            </div>
          ) : (
            <button onClick={() => upgrade(annual ? "business_plus_annual" : "business_plus_30d")} disabled={!!loading}
              data-testid={annual ? "upgrade-plus-annual-btn" : "upgrade-plus-btn"}
              className="group/cta w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2">
              {loading.startsWith("business_plus") ? <Loader2 className="animate-spin" size={18} /> : <Building2 size={18} />}
              {annual ? t("prc.goYearly", { price: PRICES.plusYear }) : t("prc.goMonthly", { price: PRICES.plus })}
              <ArrowRight size={17} className="transition-transform duration-300 group-hover/cta:translate-x-1" />
            </button>
          )}
          {tier !== "business_plus" && firstTermNote(annual ? "business_plus_annual" : "business_plus_30d") && (
            <p className="mt-2 text-center text-xs font-semibold text-[#4ade80]" data-testid="plus-first-term-note">
              {firstTermNote(annual ? "business_plus_annual" : "business_plus_30d")}
            </p>
          )}
          {tier !== "business_plus" && (
            <p className="mt-2 text-center text-[11px] text-white/70" data-testid="plus-fee-note">{t("prc.feeNote")}</p>
          )}
          <SwitchAtEnd plan="business_plus" dark />
          {!paid && (
            <p className="mt-3 text-center text-xs text-white/80 flex items-center justify-center gap-1.5" data-testid="plus-guarantee">
              <RotateCcw size={12} className="text-[#4ade80]" /> {t("prc.guarantee")} <Link to="/terms" className="underline font-semibold hover:text-white">{t("prc.guaranteeLink")}</Link>
            </p>
          )}
        </div>
      </div>

      {user?.role === "business" && (
        <div className="rounded-2xl border border-border bg-card p-4 flex flex-wrap items-center gap-3" data-testid="pricing-promo">
          <span className="text-sm font-semibold text-foreground">{t("pc.haveCode")}</span>
          <PromoCodeField promo={promo} onApply={setPromo} compact renewNote={t("prc.promoTermOnly")} />
        </div>
      )}

      <TopUpShelf addons={cfg?.addons || []} credits={credits} canBuy={user?.role === "business"} promo={promo} />

      <AddonShelf addons={cfg?.addons || []} credits={credits} verified={!!user?.verified} boostPrice={cfg?.boostPrice ?? 10} featurePrice={cfg?.featurePrice ?? 10} promo={promo} canBuy={user?.role === "business"}
        plusPerk={tier === "business_plus"} canCustomLink={tier === "business_plus" || !!user?.customLink} />

      {cfg?.stripeTestMode && user?.role === "business" && (
        <p className="text-center text-xs text-muted-foreground" data-testid="pricing-test-mode-note">{t("prc.testMode")}</p>
      )}

      <div className="rounded-2xl border border-border bg-secondary/40 p-5 text-xs text-muted-foreground space-y-1.5" data-testid="pricing-small-print">
        <p>{t("prc.sp1")}</p>
        <p>{t("prc.sp2", { pro: PRICES.pro, proYear: PRICES.proYear, plus: PRICES.plus, plusYear: PRICES.plusYear })}</p>
        <p>{t("prc.sp3")}</p>
        <p>
          {t("prc.sp4pre")} <Link to="/terms" data-testid="pricing-refund-link" className="text-[#16a34a] font-semibold hover:underline">{t("prc.sp4link")}</Link>. {t("prc.sp4post")}
        </p>
      </div>
    </div>
  );
};

export default Premium;
